import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { showInkToast } from './InkToast';
import { siteHue, SiteSummary, useTinycmsSites } from './sites';

/** The site's icon, or its initial on a tile tinted for the site. */
export function SiteTile({ site, size }: { site: SiteSummary; size: number }) {
  const radius = size >= 30 ? 8 : 6;
  if (site.iconUrl) {
    return (
      <img
        className="st-tile st-tile-img"
        src={site.iconUrl}
        alt=""
        style={{ width: size, height: size, borderRadius: radius }}
      />
    );
  }
  const hue = siteHue(site.accountId);
  return (
    <span
      className="st-tile"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        fontSize: size >= 30 ? 12 : 10,
        background: `oklch(0.93 0.05 ${hue})`,
        color: `oklch(0.42 0.12 ${hue})`,
      }}
    >
      {(site.name.trim()[0] || '?').toUpperCase()}
    </span>
  );
}

/**
 * The header's site menu ("tinycms UX optimization review v6.zip"): every
 * site the user belongs to, with its address and what's waiting on them, then
 * All sites (where New site is). Picking a site switches the whole app to it.
 */
export function SiteSwitcher() {
  const navigate = useNavigate();
  const { selectedAccount, selectAccountById } = useAccount();
  const { sites, refetch } = useTinycmsSites();
  const [open, setOpen] = useState(false);

  const current = sites.find((s) => s.accountId === selectedAccount?.id);
  const label = current?.name || selectedAccount?.name || 'Your site';

  const toggle = () => {
    if (!open) void refetch();
    setOpen((v) => !v);
  };

  const pick = (site: SiteSummary) => {
    setOpen(false);
    if (site.accountId !== selectedAccount?.id) {
      selectAccountById(site.accountId);
      showInkToast(`Switched to ${site.name}.`);
    }
    navigate('/today');
  };

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <span className="st">
      <button
        type="button"
        className="st-btn"
        data-open={open ? 'true' : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <span className="st-btn-name">{label}</span>
        <span className="st-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open ? (
        <>
          <div className="st-scrim" onClick={() => setOpen(false)} />
          <div className="st-menu" role="menu">
            <div className="st-menu-label">SITES</div>
            {sites.map((site) => {
              const here = site.accountId === selectedAccount?.id;
              return (
                <button
                  key={site.accountId}
                  type="button"
                  role="menuitemradio"
                  aria-checked={here}
                  className="st-row"
                  onClick={() => pick(site)}
                >
                  <SiteTile site={site} size={30} />
                  <span className="st-row-text">
                    <span className="st-row-name">{site.name}</span>
                    <span className="st-row-host">{site.host || 'No address yet'}</span>
                  </span>
                  {site.waiting > 0 ? (
                    <span className="st-need" title="Waiting on you">
                      {site.waiting}
                    </span>
                  ) : null}
                  <span className="st-check" aria-hidden="true">
                    {here ? '✓' : ''}
                  </span>
                </button>
              );
            })}
            <div className="st-rule" />
            <button type="button" className="st-link" role="menuitem" onClick={() => go('/sites')}>
              All sites
            </button>
          </div>
        </>
      ) : null}
    </span>
  );
}
